import DeleteIcon from "@mui/icons-material/DeleteOutline";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import { styled, Theme } from "@mui/material";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  ConfirmResponse,
  useConfirmDialog,
} from "app/providers/confirm-dialog";
import { useDialog } from "app/providers/dialog";
import { CanceledError } from "axios";
import { OrderDirection } from "common/contracts/order-direction";
import PaginationParameters from "common/contracts/pagination-parameters";
import { AccountType } from "common/values/account-type/account-type";
import Guid from "common/values/guid/guid";
import ViewCompanyProfile from "marketplace/values/company-profile/view/view-company-profile";
import ViewIndividualProfile from "marketplace/values/individual-profile/view/view-individual-profile";
import {
  createMRTColumnHelper,
  MaterialReactTable,
  type MRT_Row as Row,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  type MRT_SortingState as SortingState,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  useMaterialReactTable,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import * as React from "react";
import { useEffect } from "react";
import {
  useLoaderData,
  useNavigate
} from "react-router-dom";
import { useSession } from "users/session/session-context";
import WorkTeamTemplateQuery, { CreatorType, WorkTeamTemplateFilterParameters, WorkTeamTemplateOrderField, WorkTeamTemplateOrderParameters } from "work/entities/team-template/api/request-contracts/query-work-team-template-api-request";
import TeamTemplateAPIService from "work/entities/team-template/api/team-template-service";
import TeamTemplate from "work/entities/team-template/team-template";
import TeamTemplateForm from "work/entities/team-template/view/team-template-form";
import { PageTab } from "./tabs";
import { NotAuthorized } from "common/components/not-authorized";

const TeamColumnLink = styled(Link)(
  ({ theme }) => ({
    color: theme.palette.common.black,
    display: "block",
    textDecorationColor: theme.palette.common.black,
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  }));
const ToolbarButtonsContainer = styled("div")(
  ({ theme }) => ({
    [theme.breakpoints.down('md')]: {
      flexDirection: "row",
      justifyContent: "space-between",
      width: "100%",
    },
    display: "flex",
    alignItems: "end",
    flexDirection: "column",
    gap: theme.spacing(1),
  }));
const ToolbarButtons = styled("div")(
  () => ({
    display: "flex",
    alignItems: "end",
    flexWrap: "nowrap"
  }));
const TableTopToolbarContainer = styled("div")(
  () => ({
    display: "flex",
    flexDirection: "column",
    width: "100%",
  }));
const LowerTopToolbarContainer = styled("div")(
  ({ theme }) => ({
    [theme.breakpoints.down('md')]: {
      flexDirection: "column",
    },
    display: "flex",
    flexDirection: "row",
    alignItems: "left",
    width: "100%",
  }));
const TableTitle = styled(Typography)(
  ({ theme }) => ({
    paddingLeft: theme.spacing(1),
  }));
const RowTitle = styled(Typography)(
  () => ({
    fontSize: "1.1em",
    fontWeight: "600",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }));
const NewButton = styled(IconButton)(
  () => ({
    padding: 0
  }));

const NameCell = ({ row }: { row: Row<TeamTemplate> }) => (
  <RowTitle>{row.original.name.value}</RowTitle>
);

export default function Teams() {
  const isDirtyRef = React.useRef<boolean>(false);

  const [isLoading, setIsLoading] = React.useState(false);
  const [isRefetching, setIsRefetching] = React.useState(false);
  const [data, setData] = React.useState<TeamTemplate[]>([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [wasErrorLoading, setWasErrorLoading] = React.useState(false);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [globalFilter, setGlobalFilter] = React.useState();

  const [activeTab, setActiveTab] = React.useState(PageTab.Templates);

  const routeParams = useLoaderData();
  const navigate = useNavigate();
  const session = useSession();
  const { openDialog, closeDialog } = useDialog();
  const confirm = useConfirmDialog();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  // Handle URL changes
  useEffect(
    () => {
      if (!session.user?.companyEntityId || !session.user?.isCompanyManager) return;

      if (routeParams.tab) {
        setActiveTab(routeParams.tab as PageTab);
      } else {
        navigate(`/teams/${PageTab.Templates}`);
      }

      if (routeParams.action === "create") {
        beginCreateTeamTemplate();
        return;
      }

      if (routeParams.tab === PageTab.Templates && routeParams.id) {
        handleEditTeamTemplate(new Guid(routeParams.id));
      }

      let abortController = new AbortController();
      void fetchData(routeParams.tab as PageTab, abortController);

      return () => {
        abortController.abort();
        abortController = new AbortController();
      };
    },
    [
      routeParams,
      columnFilters,
      globalFilter,
      pagination.pageIndex,
      pagination.pageSize,
      sorting
    ]
  );

  const fetchData = async (tab?: PageTab, abortController?: AbortController) => {
    if (!data.length) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }

    try {
      if (session.hasEntity) {
        const teamTemplateService = new TeamTemplateAPIService(session);
        const filterParameters = new WorkTeamTemplateFilterParameters(
          session.entities[0].entityId,
          columnFilters.find((filter) => filter.id === "name")?.value as string,
          columnFilters.find((filter) => filter.id === "leader")?.value as string,
          columnFilters.find((filter) => filter.id === "members")?.value as string,
          columnFilters.find((filter) => filter.id === "company")?.value as string,
          session.accountType === AccountType.Vendor ?
            CreatorType.Vendor : CreatorType.Client,
          globalFilter,
          tab === PageTab.Archived
        );
        const orderParameters = new WorkTeamTemplateOrderParameters(
          WorkTeamTemplateOrderField.Name,
          sorting[0]?.desc === false ? OrderDirection.Ascending : OrderDirection.Descending
        );
        const query = new WorkTeamTemplateQuery(
          filterParameters,
          orderParameters,
          new PaginationParameters(
            pagination.pageIndex,
            pagination.pageSize
          )
        );

        const templates = await teamTemplateService.getTeamTemplates(query, abortController);

        setData(templates.data);
        setRowCount(templates.totalElements);
      }
    } catch (error) {
      if (error instanceof CanceledError) {
        return;
      }
      setWasErrorLoading(true);
      console.error(error);
      return;
    }
    setWasErrorLoading(false);
    setIsLoading(false);
    setIsRefetching(false);
  };

  function beginCreateTeamTemplate() {
    const parentPath = new URL(
      ".",
      globalThis.location.origin + globalThis.location.pathname
    );

    openDialog(
      {
        title: "Create Template Team",
        MuiProps: { fullWidth: false, maxWidth: "lg" },
        contentSxProps: { display: "flex" },
        isDirtyRef: isDirtyRef,
        disableSave: true,
        component: (
          <TeamTemplateForm
            leader={null}
            members={[]}
            navigate={navigate}
            onFormDirty={(dirty) => {
              isDirtyRef.current = dirty === true;
            }}
            onSave={() => {
              isDirtyRef.current = false;
              closeDialog();
              navigate(parentPath.pathname);
              void fetchData();
            }}
          />
        ),
        onClose: () => {
          isDirtyRef.current = false;
          closeDialog();
        }
      },
      () => navigate(parentPath.pathname),
    );
  }

  function handleEditTeamTemplate(id: Guid) {
    const template = data.find((template) => template.id?.isEqualTo(id));

    if (!template) {
      enqueueSnackbar(
        "Team template not found",
        { variant: "error" }
      );
      return;
    }

    const parentPath = new URL(
      ".",
      globalThis.location.origin + globalThis.location.pathname
    );

    openDialog(
      {
        title: "Edit Template Team",
        MuiProps: { fullWidth: false, maxWidth: "lg" },
        contentSxProps: { display: "flex" },
        isDirtyRef: isDirtyRef,
        disableSave: true,
        component: (
          <TeamTemplateForm
            template={template}
            name={template.name.value}
            leader={template.leader}
            members={template.members}
            navigate={navigate}
            onFormDirty={(dirty) => {
              isDirtyRef.current = dirty === true;
            }}
            onSave={() => {
              isDirtyRef.current = false;
              closeDialog();
              navigate(parentPath.pathname);
              void fetchData();
            }}
          />
        ),
        onClose: () => {
          isDirtyRef.current = false;
          closeDialog();
        }
      },
      () => navigate(parentPath.pathname),
    );
  }

  async function handleDeleteTeamTemplate(id: Guid) {
    try {
      const response = await confirm({
        title: "Delete?",
        message: `Delete team template?`,
        okButtonText: "Delete",
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;

      const teamTemplateService = new TeamTemplateAPIService(session);
      await teamTemplateService.deleteTeamTemplate(id);
      enqueueSnackbar(
        "Deleted vendor-team template",
        { variant: "success" }
      );
      await fetchData();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        "Failed to delete vendor-team template. Please try again",
        {
          variant: "error",
        }
      );
    }
  }

  const renderLeaderColumn = (team: TeamTemplate) => {
    if (!team.leader) return null;
    return (
      <TeamColumnLink
        onClick={(event) => {
          event.stopPropagation();
          openDialog({
            component: <ViewIndividualProfile individualId={team.leader.id} />,
            contentSxProps: {
              display: "flex",
            },
            MuiProps: {
              fullWidth: true,
              maxWidth: "lg",
            },
            titleStyle: {
              position: "absolute",
              right: 0,
              top: 0,
            },
            onClose: closeDialog,
          });
        }}
      >
        {team.leader.getFullName()}
      </TeamColumnLink>
    );
  };

  const renderMembersColumn = (team: TeamTemplate) => {
    if (team.members.length === 0) {
      return <Icon>remove</Icon>;
    }
    return (
      <div>
        {team.members.map((member) => (
          <TeamColumnLink
            key={member.id.value}
            onClick={(event) => {
              event.stopPropagation();
              openDialog({
                component: <ViewIndividualProfile individualId={member.id} />,
                contentSxProps: {
                  display: "flex",
                },
                MuiProps: {
                  fullWidth: true,
                  maxWidth: "lg",
                },
                titleStyle: {
                  position: "absolute",
                  right: 0,
                  top: 0,
                },
                onClose: closeDialog,
              });
            }}
          >
            {member.getFullName()}
          </TeamColumnLink>
        ))}
      </div>
    );
  };

  const renderCompanyColumn = (team: TeamTemplate) => {
    return (
      <TeamColumnLink
        key={team.leader.company?.profile?.name}
        onClick={(event) => {
          event.stopPropagation();
          openDialog({
            component: <ViewCompanyProfile companyId={team.leader.company?.id} />,
            contentSxProps: {
              display: "flex",
            },
            MuiProps: {
              fullWidth: true,
              maxWidth: "lg",
            },
            titleStyle: {
              position: "absolute",
              right: 0,
              top: 0,
            },
            onClose: closeDialog,
          });
        }}
      >
        {team.leader.company?.profile?.name}
      </TeamColumnLink>
    );
  };

  const columnHelper = createMRTColumnHelper<TeamTemplate>();
  const columns = [
    columnHelper.accessor(
      (team) => team.name.value,
      {
        header: "Template Name",
        id: "name",
        Cell: NameCell
      }
    ),
    columnHelper.accessor(
      renderCompanyColumn,
      {
        header: "Company",
        id: "company"
      }
    ),
    columnHelper.accessor(
      renderLeaderColumn,
      {
        header: "Leader",
        id: "leader"
      }
    ),
    columnHelper.accessor(
      renderMembersColumn,
      {
        header: "Members",
        id: "members"
      }
    ),
  ];

  const table = useMaterialReactTable({
    columns,
    data,
    enableRowSelection: false,
    enableTableHead: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        const template = row.original;
        const parentPath = new URL(
          ".",
          globalThis.location.origin + globalThis.location.pathname
        );

        openDialog(
          {
            title: "Edit Template Team",
            MuiProps: { fullWidth: false, maxWidth: "lg" },
            contentSxProps: { display: "flex" },
            component: (
              <TeamTemplateForm
                template={template}
                name={template.name.value}
                leader={template.leader}
                members={template.members}
                navigate={navigate}
                onFormDirty={() => {
                  isDirtyRef.current = true;
                }}
                onSave={() => {
                  closeDialog();
                  navigate(parentPath.pathname);
                  void fetchData();
                }}
              />
            ),
          },
          () => navigate(parentPath.pathname),
        );
      },
      sx: { cursor: "pointer" },
    }),
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
            <Button
              startIcon={<GroupAddIcon />}
              onClick={() => navigate("/teams/templates/create")}>
              Create Team
            </Button>
          </ToolbarButtonsContainer>
        )}
        {isMediumDisplaySize && (
          <NewButton
            color="primary"
            onClick={() => navigate("/teams/templates/create")}>
            <GroupAddIcon fontSize="large" />
          </NewButton>
        )}
      </>
    ),
    renderTopToolbarCustomActions: () => {
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">
            Teams
          </TableTitle>
          <LowerTopToolbarContainer>
            <Tabs
              value={activeTab}
              indicatorColor="primary"
              textColor="primary"
              onChange={(_event, newValue) => navigate(`/teams/${newValue}/`)}
            >
              <Tab value='templates' label='Templates' />
              {/*<Tab value='teams'*/}
              {/*     label={session.accountType === AccountType.Client ? "Teams Working For Me" : "Teams I'm On"}/>*/}
              <Tab value='archived' label="Archived" />
            </Tabs>
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <ToolbarButtonsContainer>
              <ToolbarButtons>
                <ToggleGlobalFilterButton table={table} />
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleDensePaddingButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </ToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    getRowId: (row) => row.id?.toString() ?? Guid.generate().toString(),
    enableRowActions: true,
    enableColumnPinning: true,
    initialState: {
      columnPinning: { right: ['mrt-row-actions'] }
    },
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    renderRowActions: ({ row }) => {
      if (activeTab !== PageTab.Templates) return null;
      return (
        <Tooltip title="Delete Template">
          <span>
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                if (!row.original.id) return;
                void handleDeleteTeamTemplate(row.original.id);
              }}
            >
              <DeleteIcon color="error" />
            </IconButton>
          </span>
        </Tooltip>
      );
    },
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiToolbarAlertBannerProps: wasErrorLoading
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount,
    state: {
      columnFilters,
      globalFilter,
      isLoading,
      pagination,
      showAlertBanner: wasErrorLoading,
      showProgressBars: isRefetching,
      sorting
    },
  });

  if (!session.user?.companyEntityId || !session.user?.isCompanyManager) {
    return <NotAuthorized />;
  }

  return <MaterialReactTable table={table} />;
}
